import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { LoadPanel } from 'devextreme-react';
import { HistoricalResultsApis } from 'hooks/apis/HistoricalResultsApis';
import { IReportAccess } from 'interfaces/HistoricalResults/IReportAccess';

const HistoricalResultsAccess = () => {
  const { getAccess, postRequestAccess } = HistoricalResultsApis();
  const [reportAccess, setReportAccess] = useState<IReportAccess[]>(null);
  const [sendingRequest, setSendingRequest] = useState<boolean>(false);
  const [error, setError] = useState<string>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const pendingResults = useMemo(
    () => reportAccess?.filter((f) => f.RequestedAccess === true && f.HasAccess === false && f.IsManager === false),
    [reportAccess]
  );
  const requestResults = useMemo(
    () => reportAccess?.filter((f) => f.RequestedAccess === false && f.HasAccess === false && f.IsManager === false),
    [reportAccess]
  );

  const refreshData = useCallback(() => {
    getAccess().then((response) => {
      if (response && !response.errorMessage) {
        setReportAccess(response);
      } else {
        setError(response.errorMessage);
      }
    });
  }, [getAccess]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const requestAccess = useCallback(
    (id) => {
      setSendingRequest(true);
      postRequestAccess(id).then((response) => {
        if (response && response.message && response.message === 'Success') {
          refreshData();
        } else {
          setError(response.errorMessage);
        }
      }).finally(() => {
        setSendingRequest(false);
      });
    },
    [postRequestAccess, refreshData]
  );

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  return (
    <>
      <Button onClick={() => setDialogOpen(true)}>Request access to online results</Button>
      <Dialog open={dialogOpen} onClose={() => closeDialog()}>
        <DialogTitle>Request Access to Online Results</DialogTitle>
        <DialogContent>
          <LoadPanel height={100} width={250} message="Sending access request" visible={sendingRequest} />
          <Box className="requestHistorical">
            {error !== null ? <p className="errorMessage">{error}</p> : null}
            {requestResults?.length > 0 ? (
              <Box>
                <h2>Request Access</h2>
                {requestResults?.map((m) => (
                  <Box className="clientBox" key={m.ClRef + m.Branch}>
                    <Box>
                      <h3>{m.ClRef} | {m.Branch}</h3>
                      <p>{m.ClientName}<br/>{m.ClientAddress}</p>
                    </Box>

                    <Box><Button className="siteBtn" onClick={() => requestAccess(m.Id)}>Request Access</Button></Box>                    
                  </Box>
                ))}
              </Box>
            ) : null}
            {pendingResults?.length > 0 ? (
              <Box>
                <h2>Pending Access</h2>
                {pendingResults.map((m) => (
                    <Box className="pendingBox" key={m.ClRef + m.Branch}>
                        <h3>{m.ClRef} | {m.Branch}</h3>
                        <p>{m.ClientName}<br/>{m.ClientAddress}</p>
                    </Box>
                  ))}
              </Box>
            ) : null}

            {pendingResults?.length === 0 && requestResults?.length === 0 ? <Box><p>No available clients to request</p></Box> : null}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HistoricalResultsAccess;
